import {
  FC,
  Fragment,
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';

import {
  HeaderWrapper,
  MainTitleContainer,
  ResetButtonLabel,
  ResetButtonWrapper,
  SearchTeamMembersWrapper,
  StyledJanesWrapper,
  BKJSideModalFooterContainer,
  BKJSideModalFooterContent,
  BKJSideModalFooterConfirmationButton,
  TotalBidPlacedText,
} from './AwardTeamMemberView.styled';
import { Flex } from 'components/Flex';
import { MainTitle } from 'pages/CreateShiftsPage/BulkCreateShiftView/CreateShiftModal/src/views/CreateShiftModal.styled';
import { UserGuard } from 'guards/UserGuard';
import { useTranslation } from 'hooks/useTranslation';
import {
  BKJButton,
  BKJButtonIconTextInput,
  BKJTextInput,
} from '@bookjane2/bookjane-design-library';
import { EmploymentStatusFilterInput } from 'components/EmploymentStatusFilterInput';
import { AwardTeamMemberFilterBar } from 'modals/ShiftModal/src/views/AwardTeamMemberView/AwardTeamMemberFilterBar';
import {
  AwardTeamMemberContext,
  AwardTeamMemberProvider,
  useAwardTeamMemberContext,
} from 'modals/ShiftModal/src/views/AwardTeamMemberView/AwardTeamMemberView.data.context';
import { ShiftModalComponentMap } from 'modals/ShiftModal/ShiftModal.constants';
import { CreateShiftCardContext } from 'pages/CreateShiftsPage/BulkCreateShiftView/CreateShiftCard/CreateShiftCard.context';
import { withMemo } from 'utils/withMemo';
import {
  useShiftModalUIContext,
  shiftModalRoutingService,
} from 'modals/ShiftModal/ShiftModal.ui.context';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { AwardJaneGridSelect } from 'components/AwardJaneGridSelect';
import {
  AwardTeamMemberViewFormProvider,
  useAwardTeamMemberViewFormContext,
} from 'modals/ShiftModal/src/views/AwardTeamMemberView/AwardTeamMemberView.form.context';
import { CSVBorderHeaderStyle } from 'components/InviteTeamMembersByCSVModal/views/ImportViaSpreadsheet/TeamMemberCSVView.styled';
import { toastService } from 'services';
import { BKJCenteredDialog } from 'components/BKJCenteredDialog';
import { css } from 'styled-components';
const RenderSSFControls: FC = () => {
  const { t } = useTranslation();
  const { values, onChange, onReset } = useAwardTeamMemberContext();
  const [isOpen, setOpen] = useState(false);

  const handleClick = useCallback(() => {
    setOpen(!isOpen);
  }, [isOpen]);

  return (
    <Flex flexDirection="column">
      <SearchTeamMembersWrapper>
        <UserGuard userType={['USER_TYPE_PREMIUM', 'USER_TYPE_PREMIUM_SECONDARY']}>
          <BKJButtonIconTextInput
            placeholder={t('shift_modal:SEARCH_MEMBER')}
            buttonLabel={t('shift_modal:FILTERS')}
            name="byMultipleFields"
            iconName="Search"
            buttonIcon="Filters"
            value={values?.byMultipleFields}
            onChange={onChange}
            onButtonClick={handleClick}
            autoComplete="off"
          />
        </UserGuard>
        <UserGuard userType={['USER_TYPE_ESSENTIAL', 'USER_TYPE_ESSENTIAL_SECONDARY']}>
          <BKJTextInput
            placeholder={t('shift_modal:SEARCH_MEMBER')}
            name="byMultipleFields"
            leftIconProps={{ iconName: 'Search' }}
            value={values?.byMultipleFields}
            onChange={onChange}
            autoComplete="off"
          />
        </UserGuard>
      </SearchTeamMembersWrapper>

      <AwardTeamMemberFilterBar isOpen={isOpen}>
        <Flex justifyContent="space-between" width="100%" alignItems="baseline">
          <UserGuard userType={['USER_TYPE_PREMIUM', 'USER_TYPE_PREMIUM_SECONDARY']}>
            <EmploymentStatusFilterInput
              name="byEmploymentStatus"
              onChange={onChange}
              value={values?.byEmploymentStatus}
              options={['casual', 'full_time', 'part_time', 'flex']}
            />
          </UserGuard>
          <UserGuard userType={['USER_TYPE_PREMIUM', 'USER_TYPE_PREMIUM_SECONDARY']}>
            <ResetButtonWrapper onClick={onReset as MouseEventHandler<HTMLDivElement>}>
              <ResetButtonLabel>{t('shift_modal:RESET')}</ResetButtonLabel>
            </ResetButtonWrapper>
          </UserGuard>
        </Flex>
      </AwardTeamMemberFilterBar>
    </Flex>
  );
};
function RenderJanes() {
  const { handleChange, selectedJanes, loadMore } = useAwardTeamMemberViewFormContext();
  const { createShift } = useContext(CreateShiftCardContext);
  const {
    values: { quantity },
  } = createShift;
  return (
    <AwardJaneGridSelect
      paginationType="InfiniteScroll"
      type={parseInt(quantity[0]) > 1 ? 'Multiple' : 'Single'}
      max={parseInt(quantity[0])}
      name="request_janes"
      dataPath="order.bids"
      value={selectedJanes}
      context={AwardTeamMemberContext}
      onChange={handleChange}
      loadMore={loadMore}
      pageInfoDataPath="order.pageInfo"
    />
  );
}
export const AwardTeamMemberView: FC = () => {
  const { data, biddingStatus } = useAwardTeamMemberContext();
  const { data: modalData } = useShiftModalDataContext();
  const order = data?.order;
  const { selectedJanes, handleDenyAllBids, onSubmit, hasConflictedShifts } =
    useAwardTeamMemberViewFormContext();
  const bidsLength = data && order?.bids?.length;
  const [isShowAlreadyAwardedToast, setIsShowAlreadyAwardedToast] =
    useState<Nullable<boolean>>(null);
  const isDisable = biddingStatus === 'AWARDING' && selectedJanes?.length !== 0 ? false : true;
  const { t } = useTranslation();

  const [isDenyAllBidsConfirmationOpen, setIsDenyAllBidsConfirmationOpen] = useState(false);

  const isBlockShift = !!modalData?.order?.isBlockShift;
  const isBlockLeadShift = !!modalData?.order?.isBlockLeadShift;

  const handleConfirmClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (hasConflictedShifts) {
        shiftModalRoutingService.goToView('ShiftConflictView', {
          selectedJanes: selectedJanes.join(','),
        });
        return;
      }
      if (isBlockShift) {
        shiftModalRoutingService.goToView('AwardShiftBlockView');
        return;
      }
      onSubmit(e);
    },
    [hasConflictedShifts, isBlockShift, onSubmit, selectedJanes],
  );

  useEffect(() => {
    if (
      !!order &&
      isShowAlreadyAwardedToast !== false &&
      biddingStatus &&
      biddingStatus !== 'AWARDING'
    ) {
      setIsShowAlreadyAwardedToast(true);
    }
    if (isShowAlreadyAwardedToast !== null && isShowAlreadyAwardedToast) {
      setIsShowAlreadyAwardedToast(false);
      toastService.error(t('shift_modal:UNABLE_TO_AWARD'));
    }
  }, [biddingStatus, order, isShowAlreadyAwardedToast, t]);

  return (
    <Fragment>
      <HeaderWrapper flexDirection="column" borderTop={CSVBorderHeaderStyle}>
        <MainTitleContainer justifyContent="space-between" alignItems="baseline">
          <MainTitle>{t('shift_modal:AWARD_TO_TEAM_MEMBER')}</MainTitle>
          <TotalBidPlacedText>
            {bidsLength} {t('shift_modal:PLACED_BID')}
          </TotalBidPlacedText>
        </MainTitleContainer>
        <RenderSSFControls />
      </HeaderWrapper>
      <StyledJanesWrapper width="100%" flex="1" padding="0px 24px 0px 32px">
        <RenderJanes />
      </StyledJanesWrapper>
      <BKJSideModalFooter>
        <BKJSideModalFooterContainer
          justifyContent="space-between"
          width="100%"
          alignItems="center"
        >
          <BKJSideModalFooterContent flexDirection="column">
            {(!isBlockShift || isBlockLeadShift) && (
              <div>
                <BKJButton
                  width="auto"
                  variant="RedText"
                  css={css`
                    text-decoration: none;
                    font-weight: 600;
                  `}
                  onClick={() => setIsDenyAllBidsConfirmationOpen(true)}
                >
                  {t('shift_modal:DENY_ALL_BIDS_AND_EXTEND')}
                </BKJButton>
              </div>
            )}
          </BKJSideModalFooterContent>
          <BKJSideModalFooterConfirmationButton
            variant={'PurpleSolid'}
            onClick={handleConfirmClick}
            disabled={isDisable}
          >
            {isBlockShift ? t('shift_modal:NEXT') : t('shift_modal:CONFIRM')}
          </BKJSideModalFooterConfirmationButton>
        </BKJSideModalFooterContainer>
      </BKJSideModalFooter>
      <BKJCenteredDialog
        isOpen={isDenyAllBidsConfirmationOpen}
        variant="Danger"
        title={t('shift_modal:DENY_ALL_BIDS_CONFIRMATION_TITLE')}
        text={t('shift_modal:DENY_ALL_BIDS_CONFIRMATION_BODY')}
        cancelLabel={t('auth:CANCEL')}
        actionLabel={t('shift_modal:DENY_ALL_BIDS_AND_EXTEND')}
        onAction={() => {
          setIsDenyAllBidsConfirmationOpen(false);
          return handleDenyAllBids();
        }}
        onCancel={() => setIsDenyAllBidsConfirmationOpen(false)}
        onClose={() => setIsDenyAllBidsConfirmationOpen(false)}
      />
    </Fragment>
  );
};
export const AwardTeamMemberViewRender: FC = withMemo((props) => {
  const { shiftModalActiveModalView } = useShiftModalUIContext();
  const Component = {
    ...ShiftModalComponentMap,
    AwardTeamMemberView,
  }[shiftModalActiveModalView];

  return (
    <AwardTeamMemberProvider>
      <AwardTeamMemberViewFormProvider>
        <Component {...props} />
      </AwardTeamMemberViewFormProvider>
    </AwardTeamMemberProvider>
  );
});
